/*
 * The console's own card, under a name of its own.
 *
 * `card.tsx` is the fleet's file now, and the fleet's `Card` is a different
 * thing: a panel a screen sits *in*, with a header, a description and a footer.
 * This one is the signed-out card — a 400 pixel panel that *is* the screen —
 * and the shape it draws is the one already on production. It stays because
 * §5 leaves the signed-out screens out of the administration's shape; what it
 * cannot do is sit on the fleet's name.
 *
 * Its uses beyond signing in are the same panel in the same role: a screen
 * that is one message (404, "no such site", "no such workspace").
 */
import { cn } from "@/lib/cn";

export function LoginCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "w-full max-w-[400px] rounded-xl border border-border bg-card p-8",
        className,
      )}
      {...props}
    />
  );
}

/**
 * `font-bold` is not decoration here, it is the browser's own `h1` weight,
 * which Tailwind's preflight resets away. The old console never declared it —
 * it did not have to — so the heading arrived in the flip a whole weight
 * lighter than production, on every screen at once.
 */
export function LoginCardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h1
      className={cn("mt-[.2rem] mb-[.3rem] text-2xl font-bold tracking-[-.02em]", className)}
      {...props}
    />
  );
}

export function LoginCardSubtitle({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("mt-0 mb-6 text-lg text-muted-foreground", className)} {...props} />;
}

/**
 * A message the person is meant to read and act on.
 *
 * `role="status"` rather than a styled paragraph: these appear in place of a
 * screen the person was expecting, so they have to be announced.
 */
export function Notice({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="status"
      className={cn(
        "rounded-lg border border-border bg-primary/10 p-[.9rem] text-base text-foreground",
        className,
      )}
      {...props}
    />
  );
}
